import { useMemo } from 'react';

import { attackSurfaceOptions } from '@/app/constant';
import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { useStatisticsBatch } from '@/hooks/useStatisticsBatch';
import type { DropdownOption } from '@/types/dropdown.types';
import { useMergeStatus } from '@/utils/api';
import { excludeFilters, FilterContext } from '@/utils/filterExclusion.util';
import { getShortName } from '@/utils/integrationDisplay.util';
import { ASSET_STATUS_OPTIONS as AssetStatusOptions } from '@/utils/mappings.util';
import { transformSourceFilterOptions } from '@/utils/originDisplay.util';
import {
  isValidBasicValue,
  isValidPortValue,
  normalizeAsnameValue,
  normalizeCountryValue,
} from '@/utils/statisticValidation.util';

// Type for filter options with additional properties
export type FilterOption = DropdownOption & {
  key?: string;
};

// Type for grouped options
export type GroupOption = FilterOption & {
  isGroup?: boolean;
  items?: FilterOption[];
};

// Return type for the hook - ALPHABETICALLY ORDERED
type AssetFilterOptions = {
  new: FilterOption[];
  asname: FilterOption[];
  asnumber: FilterOption[];
  attackSurface: FilterOption[];
  class: FilterOption[];
  country: FilterOption[];
  domain: FilterOption[];
  origins: (FilterOption | GroupOption)[];
  port: FilterOption[];
  protocol: FilterOption[];
  status: FilterOption[];
  tags: FilterOption[];
};

export const useAssetFilterOptions = (): {
  data: AssetFilterOptions;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  /**
   * PERFORMANCE OPTIMIZATION: Batch statistics query
   *
   * All 8 statistic keys are fetched in a single batch request for maximum performance.
   * The backend supports MultiValueQueryStringParameters["key"] and processes
   * all keys in parallel via errgroup.
   *
   * Batched queries:
   * - asset#origin
   * - asset#asname
   * - my##attribute#port##now
   * - my##attribute#protocol##now
   * - asset#asnumber
   * - asset#country
   * - asset#tags
   * - asset#domain
   *
   * Hardcoded options for: attackSurface, class, status, new
   * Separate endpoint for: capabilities
   */
  const { data: batchStatistics, status: batchStatisticsStatus } =
    useStatisticsBatch({
      statisticKeys: [
        'asset#origin',
        'asset#asname',
        'my##attribute#port##now',
        'my##attribute#protocol##now',
        'asset#asnumber',
        'asset#country',
        'asset#tags',
        'asset#domain',
      ],
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5 minutes (matches original queries)
    });

  // Fetch full capability data to get the integration metadata (separate endpoint)
  const { capabilities: fullCapabilities, status: capabilitiesStatus } =
    useGetCapabilities();

  // Fetch scan profiles for profile filter (client-side derived, not from statistics)
  const { profiles: scanProfiles } = useScanProfiles();

  const options = useMemo(() => {
    // Extract individual statistics from batch response with fallback to empty arrays
    const originsStatistics = batchStatistics?.['asset#origin'] || [];
    const asnameStatistics = batchStatistics?.['asset#asname'] || [];
    const portStatistics = batchStatistics?.['my##attribute#port##now'] || [];
    const protocolStatistics =
      batchStatistics?.['my##attribute#protocol##now'] || [];
    const asnumberStatistics = batchStatistics?.['asset#asnumber'] || [];
    const countryStatistics = batchStatistics?.['asset#country'] || [];
    const tagStatistics = batchStatistics?.['asset#tags'] || [];
    const dnsStatistics = batchStatistics?.['asset#domain'] || [];

    // Derive status for backward compatibility in origins logic below
    const originsStatisticsStatus = batchStatisticsStatus;

    // ALPHABETICALLY ORDERED RETURN OBJECT
    return {
      new: [
        {
          id: 'last-24-hours',
          label: 'Last 24 hours',
          key: 'asset#created#24hours',
          value: '24 hours',
        },
      ],
      asname:
        asnameStatistics && asnameStatistics.length > 0
          ? asnameStatistics
              .filter(isValidBasicValue)
              .sort((a, b) => a.value.localeCompare(b.value))
              .map((statistic, index) => {
                return {
                  id: `${statistic.name}-${index}`,
                  label: normalizeAsnameValue(statistic.value),
                  value: statistic.value,
                  key: `${statistic.name}${statistic.value}`,
                };
              })
          : [],
      asnumber:
        asnumberStatistics && asnumberStatistics.length > 0
          ? asnumberStatistics
              .filter(isValidBasicValue)
              .sort((a, b) => parseInt(a.value) - parseInt(b.value))
              .map((statistic, index) => {
                return {
                  id: `${statistic.name}-${index}`,
                  label: statistic.value,
                  value: statistic.value,
                  key: `${statistic.name}${statistic.value}`,
                };
              })
          : [],
      attackSurface: attackSurfaceOptions.map(option => ({
        id: option.id,
        label: option.label,
        value: option.value,
        key: `attackSurface${option.value}`,
      })),
      class: [
        {
          id: 'ipv4',
          label: 'IPv4',
          value: 'ipv4',
          key: 'class#ipv4',
        },
        {
          id: 'ipv6',
          label: 'IPv6',
          value: 'ipv6',
          key: 'class#ipv6',
        },
      ],
      domain:
        dnsStatistics && dnsStatistics.length > 0
          ? dnsStatistics
              .filter(isValidBasicValue)
              .sort((a, b) => a.value.localeCompare(b.value))
              .map((statistic, index) => ({
                id: `domain-${index}`,
                label: statistic.value,
                value: statistic.value,
                key: `dns#${statistic.value}`,
              }))
          : [],
      country:
        countryStatistics && countryStatistics.length > 0
          ? countryStatistics
              .filter(isValidBasicValue)
              .sort((a, b) =>
                normalizeCountryValue(a.value).localeCompare(
                  normalizeCountryValue(b.value)
                )
              )
              .map((statistic, index) => {
                return {
                  id: `${statistic.name}-${index}`,
                  label: normalizeCountryValue(statistic.value),
                  value: statistic.value,
                  key: `${statistic.name}${statistic.value}`,
                };
              })
          : [],
      origins: (() => {
        // Process origins statistics (same as Asset table logic)
        if (originsStatisticsStatus === 'pending' || !originsStatistics)
          return [];

        const originsOptions: (FilterOption | GroupOption)[] = [];

        // Create a map of capability names to their integration status
        const capabilityIntegrationMap = new Map<string, boolean>();
        if (fullCapabilities) {
          fullCapabilities.forEach(cap => {
            capabilityIntegrationMap.set(cap.name, cap.integration);
          });
        }

        // Separate into Chariot and integrations using AllIntegrations (same logic as Asset table)
        const chariotOrigins: typeof originsStatistics = [];
        const integrations: typeof originsStatistics = [];

        originsStatistics.forEach(stat => {
          const integration = Object.entries(AllIntegrations).find(
            ([key]) => key === stat.value
          );

          if (integration) {
            integrations.push(stat);
          } else {
            chariotOrigins.push(stat);
          }
        });

        // Add Praetorian as a selectable top-level option if there are Praetorian sources
        if (chariotOrigins.length > 0) {
          originsOptions.push({
            id: 'chariot-option',
            label: 'Praetorian',
            value: 'chariot',
            key: 'origin#chariot',
          });
        }

        // Add integrations as separate top-level options (same logic as Asset table)
        const integrationOptions = integrations
          .map((stat, index) => {
            // Find matching capability for backend title
            const capability = fullCapabilities?.find(
              cap => cap.name.toLowerCase() === stat.value.toLowerCase()
            );

            return {
              id: `integration-origin-${stat.value}-${index}`,
              // Use frontend SHORT_NAME_OVERRIDES mapping utility
              label: getShortName(capability?.title || stat.value, stat.value),
              value: stat.value, // Use raw backend value ("amazon"), not display name ("AWS")
              key: `origin${stat.value}`,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        originsOptions.push(...integrationOptions);

        return originsOptions;
      })(),
      ...(scanProfiles.length > 0
        ? {
            profile: [
              {
                id: 'profile-default',
                label: 'Default (No Profile)',
                value: '',
                key: 'profile#',
              },
              ...scanProfiles.map((p, index) => ({
                id: `profile-${p.id}-${index}`,
                label: p.name,
                value: p.id,
                key: `profile#${p.id}`,
              })),
            ],
          }
        : {}),
      port:
        portStatistics && portStatistics.length > 0
          ? portStatistics
              .filter(isValidPortValue)
              .sort((a, b) => parseInt(a.value) - parseInt(b.value))
              .map((statistic, index) => {
                return {
                  id: `${statistic.value}-${index}`,
                  label: statistic.value,
                  value: statistic.value,
                  key: `${statistic.name}${statistic.value}`,
                };
              })
          : [],
      protocol:
        protocolStatistics && protocolStatistics.length > 0
          ? protocolStatistics
              .filter(isValidBasicValue)
              .sort((a, b) => a.value.localeCompare(b.value))
              .map((statistic, index) => {
                return {
                  id: `${statistic.value}-${index}`,
                  label: statistic.value,
                  value: statistic.value,
                  key: `${statistic.name}${statistic.value}`,
                };
              })
          : [],
      status: AssetStatusOptions.map((option, index) => ({
        id: `status-${index}`,
        label: option.label,
        value: option.value,
        key: `status${option.value}`,
      })),
      tags: (tagStatistics || []).map((statistic, index) => ({
        id: `${statistic.name}-${index}`,
        label: statistic.value,
        value: statistic.value,
        key: `${statistic.name}${statistic.value}`,
      })),
    };
  }, [batchStatistics, batchStatisticsStatus, fullCapabilities, scanProfiles]);

  const status = useMergeStatus(batchStatisticsStatus, capabilitiesStatus);

  return {
    data: {
      ...options,
      origins: transformSourceFilterOptions(options.origins || []),
    },
    status,
  };
};

/**
 * Context-aware asset filter hook that provides scoped filter options
 *
 * This hook builds on useAssetFilterOptions to provide filtered results
 * based on the specified context, allowing different components to show
 * only the filters that make sense for their use case.
 *
 * @param context - The context in which filters will be used
 * @returns Scoped filter options and loading status
 */
export const useScopedAssetFilters = (context: FilterContext) => {
  const { data, status } = useAssetFilterOptions();

  // Memoize the exclusion to prevent unnecessary re-renders
  const filteredData = useMemo(() => {
    return excludeFilters(data, context);
  }, [data, context]);

  return {
    data: filteredData,
    status,
  };
};
